import {
  getStorageItem,
  saveItemToStorage,
  SHOPIFY_CONNECT_PENDING_KEY,
} from "@jugl-web/utils/storage";
import { environment } from "@web-src/environments/environment";
import { isShopifyStoreDomain } from "./shopifyStoreDomain";

export type ShopifyConnectPendingState = {
  entityId: string;
  createdAt: number;
};

const SHOPIFY_CONNECT_PENDING_TTL_MS = 60 * 60 * 1000;

// TODO(shopify-billing): backend to confirm whether `scope` and `state`
// (nonce) params are required — the spec only mentions client_id/redirect_uri
//
// `shop` is the URL host, so escaping it is not an option — it has to be
// validated. Returns null for anything that isn't a real Shopify store domain
// so a crafted `?shop=` can never turn this into an off-domain redirect.
export const buildShopifyAuthorizeUrl = (
  shop: string | null | undefined
): string | null => {
  if (!isShopifyStoreDomain(shop)) {
    return null;
  }

  return `https://${shop}/admin/oauth/authorize?client_id=${
    environment.shopifyClientId
  }&redirect_uri=${encodeURIComponent(
    `${window.location.origin}/shopify/callback`
  )}`;
};

/**
 * Internal-flow entry point: remember which workspace initiated the connect
 * (the OAuth redirect can't carry it) and hand the user off to Shopify.
 * `shop` must already be a full store host, e.g. `my-store.myshopify.com`.
 */
export const startShopifyConnect = (entityId: string, shop: string) => {
  const authorizeUrl = buildShopifyAuthorizeUrl(shop);

  if (!authorizeUrl) {
    return;
  }

  saveItemToStorage<ShopifyConnectPendingState>(SHOPIFY_CONNECT_PENDING_KEY, {
    entityId,
    createdAt: Date.now(),
  });
  window.location.href = authorizeUrl;
};

export const consumeShopifyConnectPending =
  (): ShopifyConnectPendingState | null => {
    const pending = getStorageItem<ShopifyConnectPendingState>(
      SHOPIFY_CONNECT_PENDING_KEY
    );
    localStorage.removeItem(SHOPIFY_CONNECT_PENDING_KEY);
    if (
      !pending ||
      Date.now() - pending.createdAt > SHOPIFY_CONNECT_PENDING_TTL_MS
    ) {
      return null;
    }
    return pending;
  };
